import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { NAV_GROUPS } from '@/app/navigation';
import { useSession } from '@/app/session';
import { Icon } from '@/design/icons';
import { Logo } from '@/design/Logo';

/**
 * The navigation rail.
 *
 * Fixed width, fixed order, never reflows — muscle memory is the whole point.
 * The active item carries the 3px signal marker on its left edge: the one
 * saturated colour in the chrome, and the app's single memorable move.
 *
 * Sections the account cannot use are absent rather than disabled. A disabled
 * row is a daily reminder of something you are not allowed to do.
 *
 * On a phone the rail becomes an overlay with the same items in the same order,
 * so there is no second navigation to learn.
 */

interface NavRailProps {
  readonly onNavigate?: () => void;
}

export function NavRail({ onNavigate }: NavRailProps): ReactElement {
  const session = useSession();
  const capabilities = session.capabilities as readonly string[];

  return (
    <nav aria-label="Sections" className="flex h-full flex-col overflow-y-auto bg-ink">
      {/* The tool's own name. The client's is in the header. */}
      <div className="border-b border-ink-inverse/10 px-4 py-3.5">
        <Logo tone="inverse" size={22} />
      </div>

      <div className="flex-1 py-3">
      {NAV_GROUPS.map((group) => {
        const visible = group.sections.filter(
          (section) => section.requires === undefined || capabilities.includes(section.requires),
        );
        if (visible.length === 0) return null;

        return (
          <div key={group.label} className="mb-4 last:mb-0">
            <h2 className="px-4 pb-1.5 text-micro font-medium uppercase text-ink-inverse/45">
              {group.label}
            </h2>
            <ul>
              {visible.map((section) => (
                  <li key={section.path}>
                    <NavLink
                      to={section.path}
                      end={section.path === '/'}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-2.5 border-l-[3px] py-2 pr-3 text-base',
                          'transition-colors',
                          isActive
                            ? 'border-l-signal-inverse bg-ink-inverse/[0.09] pl-[calc(1rem-3px)] font-semibold text-ink-inverse'
                            : 'border-l-transparent pl-[calc(1rem-3px)] text-ink-inverse/70 hover:bg-ink-inverse/[0.06] hover:text-ink-inverse',
                        ].join(' ')
                      }
                    >
                      <Icon name={section.icon} className="shrink-0 opacity-80" />
                      <span className="flex-1">{section.label}</span>
                    </NavLink>
                  </li>
              ))}
            </ul>
          </div>
        );
      })}
      </div>
    </nav>
  );
}
